import React from 'react';

import {
  Stack,
  Typography,
  IconButton,
  CircularProgress,
  Help,
  InfoOutlinedIcon,
  IndeterminateCheckBoxIcon,
  AddBoxIcon,
} from '@gogame/components';
import { useScreenState, useScreenDispatch, ScreenLink } from '@gogame/screens';

import { DIMENSION_TOTALS } from 'scenes/Trackers/Tracker/constants';
import { trackerLink } from 'scenes/Trackers/Tracker/navigation';
import {
  trackerReducer,
  TOGGLE_BREAKDOWN_EXPANDED,
  DRILL_DOWN,
} from 'scenes/Trackers/Tracker/reducers';

import Fraud from './Fraud';
import Image from './Image';
import Labels from './Labels';
import Media from './Media';
import Reactivity from './Reactivity';

window.dataLayer = window.dataLayer || [];

const DrillDownCheckBoxMockBox = () => (
  <div
    style={{
      display: 'flex',
      width: '26px',
      height: '26px',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  ></div>
);

const GroupingCellWithLazyLoading = ({
  children,
  loading,
  expandable,
  childrenExpanded,
  id,
  depth,
  onToggleRowChildrenExpansion,
  open,
}) => {
  const handleToggle = () => {
    onToggleRowChildrenExpansion({ id, depth }, !childrenExpanded);
  };

  return (
    <Stack
      direction={'row'}
      alignItems={'center'}
      sx={{ pl: `${depth * 12}px`, height: '100%' }}
    >
      <div
        style={{
          // flex: ' 0 0 28px',
          alignSelf: 'stretch',
          // marginRight: 16,
        }}
      >
        {expandable || loading ? (
          <IconButton
            edge={'start'}
            onClick={handleToggle}
            disabled={open && !childrenExpanded}
            icon={
              loading ? (
                <CircularProgress size={18} />
              ) : childrenExpanded ? (
                <IndeterminateCheckBoxIcon />
              ) : (
                <AddBoxIcon />
              )
            }
            fontSize={18}
          />
        ) : depth !== 0 ? (
          <DrillDownCheckBoxMockBox />
        ) : null}
      </div>

      {children}
    </Stack>
  );
};

const DimensionColumn = ({
  onOpenConcept,
  onOpenCreative,
  withDrillDown,
  nextDimension,
  loadingRowsIds,
  ...props
}) => {
  const { rowNode, row } = props;

  const dispatch = useScreenDispatch();
  const screenState = useScreenState();

  // const { childrenExpanded } = rowNode; // does not work correct, loaded rows without children has childrenExpanded  = false

  const handleToggleRowChildrenExpansion = ({ id, depth }, expanded) => {
    dispatch({
      type: TOGGLE_BREAKDOWN_EXPANDED,
      payload: {
        id,
        level: depth,
      },
    });

    if (expanded) {
      window.dataLayer.push({
        event: 'dimensionBreakdown',
        breakdownLevel: depth + 1,
      });
    }
  };

  const {
    fraud = { isFraud: false },
    labels = [],
    image,
    media,
    canBeExtended,
    _drillItems,
    loading,
    type,
    open,
  } = row;

  const reActivity = row['re-activity'] || {};
  const alreadyDrilledDown = _drillItems?.some(
    ({ dimension }) => dimension.id === nextDimension
  );

  const parentDrillPath2 =
    _drillItems?.map(({ dimension, drillEntity }) => ({
      id: drillEntity.id,
      type: dimension.id,
    })) || screenState.drillPath;

  const dimensionName = row.isCreativeBasedTotal ? (
    <Stack direction={'row'} spacing={0.5} alignItems={'center'}>
      <Typography
        sx={{
          fontSize: 13,
        }}
      >
        Creative-based total
      </Typography>
      <Help icon={<InfoOutlinedIcon />} cursor={'default'}>
        Aggregate metric data in creative dimensions may differ from parent
        dimension totals, as creative dimensions include data only for
        publishers or placements with creatives.
      </Help>
    </Stack>
  ) : (
    <Typography
      lineClamp={2}
      sx={{
        whiteSpace: 'normal',
        fontWeight: 'inherit',
        fontSize: 13,
        wordBreak: 'break-all',
        textAlign: 'left',
      }}
      tooltipProps={{ wrapper: true, wrapperComponent: 'div' }}
    >
      {row?.Dimension?.name}
    </Typography>
  );

  return (
    <GroupingCellWithLazyLoading
      loading={loading}
      childrenExpanded={open}
      depth={rowNode.depth}
      expandable={canBeExtended}
      id={row.id}
      // open={open}
      onToggleRowChildrenExpansion={handleToggleRowChildrenExpansion}
    >
      <Stack
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        wrap={'nowrap'}
        spacing={1}
        sx={{ width: '100%', flexWrap: 'nowrap' }}
      >
        <Stack direction={'row'} alignItems={'center'} spacing={0.5}>
          {media ? (
            <Media
              rowData={row}
              type={type}
              onOpenConcept={onOpenConcept}
              onOpenCreative={onOpenCreative}
            />
          ) : null}
          {image ? <Image {...image} /> : null}

          {(withDrillDown || nextDimension) &&
          !alreadyDrilledDown &&
          row?.Dimension?.id !== DIMENSION_TOTALS ? (
            <ScreenLink
              link={trackerLink}
              searchParams={trackerReducer(
                {
                  ...screenState,
                  drillPath: parentDrillPath2,
                },
                {
                  type: DRILL_DOWN,
                  payload: {
                    id: row?.Dimension?.id || '',
                    nextLevel: nextDimension,
                  },
                }
              )}
            >
              {dimensionName}
            </ScreenLink>
          ) : (
            dimensionName
          )}
        </Stack>
        <Stack direction={'row'} alignItems={'center'} spacing={0.5}>
          {labels.length ? <Labels labels={labels} /> : null}
          {reActivity.isAbnormal ? <Reactivity /> : null}
          {fraud.isFraud ? <Fraud redAlert={fraud.redAlert} /> : null}
        </Stack>
      </Stack>
    </GroupingCellWithLazyLoading>
  );
};

export default DimensionColumn;
